import type { ParameterRange, XYPadAxes } from '@shared/types';

export type XYPadParseResult = {
  type: 'xypad';
  xyPadAxes: XYPadAxes;
};

/**
 * Detects XY pad syntax for a 2-element numeric vector.
 *
 * Comment pattern: `[<opt-label> min:max, <opt-label> min:max]`
 *                  or `[min:step:max, min:step:max]`
 *
 * Only literal numbers are accepted for xypad axes (no $-references).
 *
 * Returns null if the comment does not match the dual-range pattern.
 */
export function parseXYPadType(
  numElements: number[],
  comment: string,
): XYPadParseResult | null {
  if (numElements.length !== 2 || !comment) return null;

  // A single range spec token: optional label word, then min:max or min:step:max
  const RS =
    '(?:([a-zA-Z][a-zA-Z0-9_]*)\\s+)?(-?\\d+(?:\\.\\d+)?)\\s*(?::\\s*(-?\\d+(?:\\.\\d+)?))?\\s*:\\s*(-?\\d+(?:\\.\\d+)?)';
  const xyPadPattern = new RegExp(
    `^\\s*\\[\\s*${RS}\\s*,\\s*${RS}\\s*\\]\\s*$`,
  );
  const xyMatch = comment.match(xyPadPattern);
  if (!xyMatch) return null;

  // Groups: 1=xLabel, 2=xFirst, 3=xMaybe, 4=xLast,
  //         5=yLabel, 6=yFirst, 7=yMaybe, 8=yLast
  const parseAxis = (
    label: string | undefined,
    first: string,
    maybe: string | undefined,
    last: string,
  ): { range: ParameterRange; label?: string } => {
    const firstNum = Number(first);
    const lastNum = Number(last);
    if (maybe !== undefined) {
      return {
        range: { min: firstNum, step: Number(maybe), max: lastNum },
        label,
      };
    }
    return { range: { min: firstNum, max: lastNum }, label };
  };

  const xAxis = parseAxis(xyMatch[1], xyMatch[2], xyMatch[3], xyMatch[4]);
  const yAxis = parseAxis(xyMatch[5], xyMatch[6], xyMatch[7], xyMatch[8]);

  return {
    type: 'xypad',
    xyPadAxes: {
      x: xAxis.range,
      y: yAxis.range,
      xLabel: xAxis.label,
      yLabel: yAxis.label,
    },
  };
}
